import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const AboutSection = () => {
  const milestones = [
    { year: '2019', event: 'Founded in Belgrade', description: 'Started with mission to humanize onboarding' },
    { year: '2021', event: 'Series A Funding', description: '€5M raised from European VCs' },
    { year: '2022', event: '10,000+ Users', description: 'Reached milestone across 30+ countries' },
    { year: '2023', event: 'Enterprise Launch', description: 'Introduced AI Buddy and workflow automation' },
    { year: '2024', event: 'SOC2 Certified', description: 'Achieved enterprise security compliance' },
    { year: '2025', event: 'Global Expansion', description: 'Opened offices in Berlin, London, NYC' },
  ];

  const teamMembers = [
    {
      name: 'Miroslav Petrović',
      role: 'CEO & Co-Founder',
      bio: 'Former HR Tech consultant with 15 years building scalable platforms',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Miroslav'
    },
    {
      name: 'Milena Jovanović',
      role: 'CTO & Co-Founder',
      bio: 'Ex-Amazon engineer specializing in AI/ML and distributed systems',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Milena'
    },
    {
      name: 'Dragomir Nikolić',
      role: 'Chief Product Officer',
      bio: 'Product leader with experience at LinkedIn and Greenhouse',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Dragomir'
    },
  ];

  const stats = [
    { value: '50,000+', label: 'Active Users', icon: 'Users' },
    { value: '85', label: 'Countries', icon: 'Globe' },
    { value: '€12M', label: 'Total Funding', icon: 'TrendingUp' },
    { value: '127', label: 'Team Members', icon: 'Briefcase' },
  ];

  return (
    <section id="about" className="scroll-mt-20">
      <div className="bg-card/30 backdrop-blur-sm border border-border rounded-2xl p-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center">
            <Icon name="Users" size={32} className="text-accent" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-foreground">About First On Date</h2>
            <p className="text-muted-foreground mt-1">Born in Belgrade. Built for the world.</p>
          </div>
        </div>

        {/* Mission Statement */}
        <div className="prose prose-invert max-w-none mb-12">
          <p className="text-xl text-foreground leading-relaxed font-medium">
            We believe every employee deserves a great first day. First On Date transforms chaotic onboarding into structured success through AI-powered assistance, systematic knowledge delivery, and gamified learning.
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed mt-4">
            Founded in Belgrade in 2019, we've grown from a small startup to a global platform serving 50,000+ users across 85 countries. Our vision: make onboarding so seamless that companies retain talent longer and employees become productive faster.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          {stats?.map((stat, index) => (
            <div 
              key={index}
              className="bg-background/50 border border-border rounded-lg p-6 text-center hover:border-accent/50 transition-colors"
            >
              <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name={stat?.icon} size={24} className="text-accent" />
              </div>
              <div className="text-3xl font-bold text-foreground mb-1">{stat?.value}</div>
              <div className="text-sm text-muted-foreground">{stat?.label}</div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-foreground mb-6">Our Journey</h3>
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-accent/20"></div>
            <div className="space-y-6">
              {milestones?.map((milestone, index) => (
                <div key={index} className="relative pl-12">
                  <div className="absolute left-0 w-8 h-8 bg-accent rounded-full flex items-center justify-center border-4 border-background">
                    <div className="w-2 h-2 bg-background rounded-full"></div>
                  </div>
                  <div className="bg-background/50 border border-border rounded-lg p-4 hover:border-accent/50 transition-colors">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-sm font-bold text-accent">{milestone?.year}</span>
                      <span className="text-foreground font-semibold">{milestone?.event}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{milestone?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Leadership Team */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-foreground mb-6">Leadership Team</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {teamMembers?.map((member, index) => (
              <div 
                key={index}
                className="bg-background/50 border border-border rounded-lg p-6 hover:border-accent/50 hover:shadow-elevation-2 transition-all text-center"
              >
                <Image 
                  src={member?.avatar}
                  alt={`${member?.name} - ${member?.role} at First On Date, experienced leader in employee onboarding technology`}
                  className="w-24 h-24 rounded-full mx-auto mb-4 border-2 border-accent/20"
                />
                <h4 className="font-bold text-foreground mb-1">{member?.name}</h4>
                <p className="text-sm text-accent mb-3">{member?.role}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{member?.bio}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Investor Info */}
        <div className="p-6 bg-accent/5 border border-accent/20 rounded-lg mb-8">
          <div className="flex items-start gap-4">
            <Icon name="TrendingUp" size={24} className="text-accent flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-foreground mb-2">Investor Relations</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Backed by leading European VCs including Index Ventures, Atomico, and Point Nine Capital. €12M total funding across Seed and Series A rounds. For investment inquiries: investors@firstondate.com
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-success/20 text-success text-xs font-semibold rounded-full">Profitable</span>
                <span className="px-3 py-1 bg-primary/20 text-primary text-xs font-semibold rounded-full">Series B Planning</span>
                <span className="px-3 py-1 bg-accent/20 text-accent text-xs font-semibold rounded-full">140% YoY Growth</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <Button 
            variant="default"
            iconName="Download"
            iconPosition="left"
            onClick={() => window.open('/press-kit.zip', '_blank')}
          >
            Download Press Kit
          </Button>
          <Button 
            variant="outline"
            iconName="FileText"
            iconPosition="left"
            onClick={() => window.open('/company-deck.pdf', '_blank')}
          >
            Company Deck
          </Button>
          <Button 
            variant="ghost"
            iconName="Mail"
            iconPosition="left"
            onClick={() => window.open('mailto:press@firstondate.com', '_blank')}
          >
            Media Inquiries
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;